/**
 * Voice API Client with React Query Hooks
 *
 * Provides type-safe API client for Voice Management System using:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 * - Proper error handling and transformations
 */

import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost, apiPut, apiDelete } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// STEP 1: Define Zod Schemas
// ============================================================================

// Voice Gender Enum
const VoiceGenderSchema = z.enum(['male', 'female', 'unclear']);

// TTS Provider Schemas
const TTSProviderSchema = z.object({
  id: z.number(),
  name: z.string().max(100),
  hasApiKey: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TTSProviderUpdateSchema = z.object({
  apiKey: z.string().min(1),
});

// Voice Schemas
const VoiceBaseSchema = z.object({
  name: z.string().max(100),
  description: z.string().min(10).max(1000),
  gender: VoiceGenderSchema,
  languageCodes: z.array(z.string().regex(/^[a-z]{2}$/)).min(1),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const VoiceCreateSchema = VoiceBaseSchema.extend({
  providerId: z.number(),
  providerVoiceId: z.string(),
  localeCode: z.string().max(10),
  sampleAudioUrl: z.string().optional(),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const VoiceUpdateSchema = z.object({
  name: z.string().max(100).optional(),
  description: z.string().min(10).max(1000).optional(),
  gender: VoiceGenderSchema.optional(),
  languageCodes: z
    .array(z.string().regex(/^[a-z]{2}$/))
    .min(1)
    .optional(),
  localeCode: z.string().max(10).nullable().optional(),
  isActive: z.boolean().optional(),
});

const VoiceResponseSchema = VoiceBaseSchema.extend({
  id: z.number(),
  providerId: z.number(),
  providerVoiceId: z.string(),
  providerName: z.string(),
  sampleAudioUrl: z.string().nullable(),
  localeCode: z.string().nullable(),
  localeName: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const VoiceDictionaryItemSchema = z.object({
  voiceId: z.number(),
  name: z.string(),
  description: z.string(),
  gender: VoiceGenderSchema,
  languageCodes: z.array(z.string()),
  localeCode: z.string().nullable(),
  localeName: z.string().nullable(),
});

// Provider Voice Item (from browse API)
const ProviderVoiceItemSchema = z.object({
  providerVoiceId: z.string(),
  name: z.string(),
  languages: z.array(z.string()),
  locale: z.string().nullable().optional(),
  metadata: z.record(z.unknown()),
});

// Audio Generation Schemas
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GenerateAudioRequestSchema = z.object({
  providerVoiceId: z.string(),
  text: z.string().max(500),
});

const GenerateAudioResponseSchema = z.object({
  audioUrl: z.string(),
  durationSeconds: z.number(),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GenerateSampleRequestSchema = z.object({
  text: z.string().max(500),
});

// Voice Attributes Generation Schemas
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GenerateVoiceAttributesRequestSchema = z.object({
  name: z.string().max(100),
  language: z.string().max(10),
  metadata: z.record(z.unknown()).default({}),
});

const GenerateVoiceAttributesResponseSchema = z.object({
  gender: z.string(),
  description: z.string(),
  localeCode: z.string().nullable().optional(),
});

// Export TypeScript types from Zod schemas
export type VoiceGender = z.infer<typeof VoiceGenderSchema>;
export type TTSProvider = z.infer<typeof TTSProviderSchema>;
export type TTSProviderUpdate = z.infer<typeof TTSProviderUpdateSchema>;
export type VoiceBase = z.infer<typeof VoiceBaseSchema>;
export type VoiceCreate = z.infer<typeof VoiceCreateSchema>;
export type VoiceUpdate = z.infer<typeof VoiceUpdateSchema>;
export type VoiceResponse = z.infer<typeof VoiceResponseSchema>;
export type VoiceDictionaryItem = z.infer<typeof VoiceDictionaryItemSchema>;
export type ProviderVoiceItem = z.infer<typeof ProviderVoiceItemSchema>;
export type GenerateAudioRequest = z.infer<typeof GenerateAudioRequestSchema>;
export type GenerateAudioResponse = z.infer<typeof GenerateAudioResponseSchema>;
export type GenerateSampleRequest = z.infer<typeof GenerateSampleRequestSchema>;
export type GenerateVoiceAttributesRequest = z.infer<typeof GenerateVoiceAttributesRequestSchema>;
export type GenerateVoiceAttributesResponse = z.infer<typeof GenerateVoiceAttributesResponseSchema>;

// Voice filters for list endpoint
export interface VoiceFilters {
  page?: number;
  limit?: number;
  isActive?: boolean;
  providerId?: number;
  gender?: VoiceGender;
  languageCode?: string;
}

// ============================================================================
// STEP 2: Define Fetcher Functions
// ============================================================================

/**
 * Transform snake_case API response to camelCase frontend types
 */
function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }

  return transformed as T;
}

/**
 * Transform camelCase frontend types to snake_case API request
 */
function transformCamelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

    if (Array.isArray(value)) {
      transformed[snakeKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformCamelToSnake(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[snakeKey] = transformCamelToSnake(value as Record<string, unknown>);
    } else {
      transformed[snakeKey] = value;
    }
  }

  return transformed;
}

// TTS Provider Fetchers
const fetchProviders = async (): Promise<TTSProvider[]> => {
  try {
    const data = await apiGet<unknown[]>('/api/admin/tts-providers');
    return z
      .array(TTSProviderSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching providers:', error);
    throw error;
  }
};

const fetchProviderVoices = async (providerId: number): Promise<ProviderVoiceItem[]> => {
  try {
    const data = await apiGet<unknown[]>(`/api/admin/tts-providers/${providerId}/voices`);
    return z
      .array(ProviderVoiceItemSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching provider voices:', error);
    throw error;
  }
};

const updateProviderApiKey = async (providerId: number, apiKey: string): Promise<TTSProvider> => {
  try {
    const payload = transformCamelToSnake({ apiKey });
    const data = await apiPut<unknown>(`/api/admin/tts-providers/${providerId}`, payload);
    return TTSProviderSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error updating provider API key:', error);
    throw error;
  }
};

const testVoice = async (
  providerId: number,
  request: GenerateAudioRequest
): Promise<GenerateAudioResponse> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>(
      `/api/admin/tts-providers/${providerId}/test-voice`,
      payload
    );
    return GenerateAudioResponseSchema.parse(
      transformSnakeToCamel(data as Record<string, unknown>)
    );
  } catch (error) {
    logger.error('Error testing voice:', error);
    throw error;
  }
};

// Voice Management Fetchers
const fetchVoices = async (filters?: VoiceFilters): Promise<VoiceResponse[]> => {
  try {
    const params = filters
      ? transformCamelToSnake(filters as unknown as Record<string, unknown>)
      : undefined;
    const data = await apiGet<unknown[]>('/api/admin/voices', params);
    return z
      .array(VoiceResponseSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching voices:', error);
    throw error;
  }
};

const fetchVoiceDictionary = async (languageCode?: string): Promise<VoiceDictionaryItem[]> => {
  try {
    const params = languageCode
      ? { language_code: languageCode, is_active: true }
      : { is_active: true };
    const data = await apiGet<unknown[]>('/api/admin/voices', params);

    // Transform VoiceResponse to VoiceDictionaryItem
    return data.map((item) => {
      const transformed = transformSnakeToCamel<Record<string, unknown>>(
        item as Record<string, unknown>
      );
      return VoiceDictionaryItemSchema.parse({
        voiceId: transformed.id,
        name: transformed.name,
        description: transformed.description,
        gender: transformed.gender,
        languageCodes: transformed.languageCodes,
        localeCode: transformed.localeCode || null,
        localeName: transformed.localeName || null,
      });
    });
  } catch (error) {
    logger.error('Error fetching voice dictionary:', error);
    throw error;
  }
};

const createVoice = async (voice: VoiceCreate): Promise<VoiceResponse> => {
  try {
    const payload = transformCamelToSnake(voice as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>('/api/admin/voices', payload);
    return VoiceResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error creating voice:', error);
    throw error;
  }
};

const updateVoice = async (voiceId: number, update: VoiceUpdate): Promise<VoiceResponse> => {
  try {
    const payload = transformCamelToSnake(update as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(`/api/admin/voices/${voiceId}`, payload);
    return VoiceResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error updating voice:', error);
    throw error;
  }
};

const deleteVoice = async (voiceId: number): Promise<void> => {
  try {
    await apiDelete<void>(`/api/admin/voices/${voiceId}`);
  } catch (error) {
    logger.error('Error deleting voice:', error);
    throw error;
  }
};

const generateVoiceSample = async (
  voiceId: number,
  text: string
): Promise<GenerateAudioResponse> => {
  try {
    const payload = { text };
    const data = await apiPost<unknown>(
      `/api/admin/voices/${voiceId}/generate-sample`,
      payload,
      30000
    );
    return GenerateAudioResponseSchema.parse(
      transformSnakeToCamel(data as Record<string, unknown>)
    );
  } catch (error) {
    logger.error('Error generating voice sample:', error);
    throw error;
  }
};

const generateVoiceAttributes = async (
  request: GenerateVoiceAttributesRequest
): Promise<GenerateVoiceAttributesResponse> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>('/api/admin/voices/generate-attributes', payload, 30000);
    return GenerateVoiceAttributesResponseSchema.parse(
      transformSnakeToCamel(data as Record<string, unknown>)
    );
  } catch (error) {
    logger.error('Error generating voice attributes:', error);
    throw error;
  }
};

// ============================================================================
// STEP 3: Define React Query Hooks
// ============================================================================

/**
 * Fetch all TTS providers
 */
export const useProviders = () => {
  return useQuery({
    queryKey: ['tts-providers'],
    queryFn: fetchProviders,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetch voices from provider API (browse mode - does not save to database)
 */
export const useProviderVoices = (providerId: number | undefined) => {
  return useQuery({
    queryKey: ['provider-voices', providerId],
    queryFn: () => fetchProviderVoices(providerId!),
    enabled: !!providerId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Fetch all voices from database with optional filters
 */
export const useVoices = (
  filters?: VoiceFilters,
  options?: Omit<UseQueryOptions<VoiceResponse[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['voices', filters],
    queryFn: () => fetchVoices(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Fetch voice dictionary (simplified list for Voice Assignment Agent)
 */
export const useVoiceDictionary = (languageCode?: string) => {
  return useQuery({
    queryKey: ['voice-dictionary', languageCode],
    queryFn: () => fetchVoiceDictionary(languageCode),
    enabled: !!languageCode, // Only fetch when languageCode is provided
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Update provider API key
 */
export const useUpdateProviderApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ providerId, apiKey }: { providerId: number; apiKey: string }) =>
      updateProviderApiKey(providerId, apiKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tts-providers'] });
    },
  });
};

/**
 * Test voice with custom text (ephemeral - no caching)
 */
export const useTestVoice = () => {
  return useMutation({
    mutationFn: ({ providerId, request }: { providerId: number; request: GenerateAudioRequest }) =>
      testVoice(providerId, request),
  });
};

/**
 * Create new voice in database
 */
export const useCreateVoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createVoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voices'] });
      queryClient.invalidateQueries({ queryKey: ['voice-dictionary'] });
    },
  });
};

/**
 * Update existing voice
 */
export const useUpdateVoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ voiceId, update }: { voiceId: number; update: VoiceUpdate }) =>
      updateVoice(voiceId, update),
    onSuccess: (updatedVoice) => {
      // Optimistic update - update the specific voice in cache
      queryClient.setQueryData(['voice', updatedVoice.id], updatedVoice);

      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: ['voices'] });
      queryClient.invalidateQueries({ queryKey: ['voice-dictionary'] });
    },
  });
};

/**
 * Delete voice (soft delete - sets isActive=false)
 */
export const useDeleteVoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteVoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voices'] });
      queryClient.invalidateQueries({ queryKey: ['voice-dictionary'] });
    },
  });
};

/**
 * Generate voice sample with custom text (ephemeral - no caching)
 */
export const useGenerateVoiceSample = () => {
  return useMutation({
    mutationFn: ({ voiceId, text }: { voiceId: number; text: string }) =>
      generateVoiceSample(voiceId, text),
  });
};

/**
 * Generate voice attributes using LLM (ephemeral - no caching)
 */
export const useGenerateVoiceAttributes = () => {
  return useMutation({
    mutationFn: (request: GenerateVoiceAttributesRequest) => generateVoiceAttributes(request),
  });
};
